/**
 * WaterfallTimeline (spec §7) — Gantt-style p50/p95/p99 bar chart.
 *
 * Each step = horizontal bar row showing:
 *   actor label (left) · stacked p50/p95/p99 bars (center) · CF kind chip (right)
 * Critical path (highest p95 step) gets a ★ marker.
 * Scheduled CFs render dashed bars to indicate async/deferred execution.
 * Pure CSS + SVG, no chart libs.
 */
import type { EnrichedSimulationTrace } from '../state/simulator.js';
import { CF_KIND_LABELS } from '../state/cfKindRouter.js';

export interface WaterfallTimelineProps {
  trace: EnrichedSimulationTrace;
}

const ROW_H = 36;
const LABEL_W = 120;
const CHIP_W = 80;
const BAR_AREA_W = 560;
const W = LABEL_W + BAR_AREA_W + CHIP_W + 24;

const SEVERITY_COLORS = {
  low: 'var(--color-success, #22c55e)',
  med: 'var(--color-warning, #f59e0b)',
  high: 'var(--color-danger, #ef4444)',
};

function latencyColor(p95: number): string {
  if (p95 > 500) return SEVERITY_COLORS.high;
  if (p95 > 100) return SEVERITY_COLORS.med;
  return SEVERITY_COLORS.low;
}

export function WaterfallTimeline({ trace }: WaterfallTimelineProps): React.JSX.Element {
  if (trace.steps.length === 0) {
    return (
      <div className="waterfall-empty" aria-label="Empty waterfall timeline">
        <p>No steps to render.</p>
      </div>
    );
  }

  const maxP99 = Math.max(...trace.steps.map((s) => s.latency?.p99 ?? 0), 1);
  const criticalHop = trace.steps.reduce(
    (best, s) => (s.latency?.p95 ?? 0) > (best.latency?.p95 ?? 0) ? s : best,
    trace.steps[0],
  );

  const H = trace.steps.length * ROW_H + 56;

  return (
    <div className="waterfall-timeline" aria-label={`Waterfall timeline for: ${trace.action.label}`}>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height: H }}>
        {/* Header */}
        <text x={LABEL_W} y={16} fontSize={10} fill="var(--text3)" fontWeight={600}>
          Latency (p50 / p95 / p99) — max scale: {maxP99} ms
        </text>

        {/* Grid line at 500ms mark */}
        {maxP99 >= 500 && (
          <line
            x1={LABEL_W + (500 / maxP99) * BAR_AREA_W}
            y1={24}
            x2={LABEL_W + (500 / maxP99) * BAR_AREA_W}
            y2={H - 16}
            stroke="#ef4444"
            strokeWidth={0.5}
            strokeDasharray="3 3"
            opacity={0.4}
          />
        )}

        {/* Step rows */}
        {trace.steps.map((step, i) => {
          const y = 28 + i * ROW_H;
          const midY = y + ROW_H / 2;
          const lat = step.latency;
          const p50W = lat ? (lat.p50 / maxP99) * BAR_AREA_W : 0;
          const p95W = lat ? (lat.p95 / maxP99) * BAR_AREA_W : 0;
          const p99W = lat ? (lat.p99 / maxP99) * BAR_AREA_W : 0;
          const color = lat ? latencyColor(lat.p95) : 'var(--text3)';
          const isScheduled = step.cfKind === 'scheduled';
          const isCritical = step.hop === criticalHop.hop;

          return (
            <g key={step.hop} className="waterfall-row">
              {/* Actor label */}
              <text
                x={LABEL_W - 6}
                y={midY + 4}
                textAnchor="end"
                fontSize={10}
                fill="var(--text2)"
                className="waterfall-label mono"
              >
                {step.source.length > 14 ? `${step.source.slice(0, 13)}…` : step.source}
              </text>

              {/* Hop number */}
              <text x={6} y={midY + 4} fontSize={9} fill="var(--text3)">{step.hop + 1}</text>

              {/* p99 bar (lightest) */}
              {p99W > 0 && (
                <rect
                  x={LABEL_W}
                  y={midY - 8}
                  width={p99W}
                  height={16}
                  rx={2}
                  fill={color}
                  opacity={0.2}
                  strokeDasharray={isScheduled ? '4 2' : undefined}
                />
              )}

              {/* p95 bar */}
              {p95W > 0 && (
                <rect
                  x={LABEL_W}
                  y={midY - 6}
                  width={p95W}
                  height={12}
                  rx={2}
                  fill={color}
                  opacity={isScheduled ? 0.35 : 0.55}
                  strokeDasharray={isScheduled ? '4 2' : undefined}
                />
              )}

              {/* p50 bar (darkest) */}
              {p50W > 0 && (
                <rect
                  x={LABEL_W}
                  y={midY - 4}
                  width={p50W}
                  height={8}
                  rx={2}
                  fill={color}
                  opacity={isScheduled ? 0.5 : 0.85}
                  strokeDasharray={isScheduled ? '4 2' : undefined}
                />
              )}

              {/* Critical path star */}
              {isCritical && lat && lat.p95 > 0 && (
                <text
                  x={LABEL_W + p95W + 4}
                  y={midY + 4}
                  fontSize={12}
                  fill={color}
                  aria-label="Critical path"
                >
                  ★
                </text>
              )}

              {/* Latency values */}
              {lat && lat.p95 > 0 && (
                <text
                  x={LABEL_W + p99W + 8 + (isCritical ? 14 : 0)}
                  y={midY + 4}
                  fontSize={9}
                  fill="var(--text3)"
                  className="mono"
                >
                  {lat.p50}/{lat.p95}/{lat.p99}
                </text>
              )}

              {/* CF kind chip */}
              {step.cfKind && (
                <g>
                  <rect
                    x={LABEL_W + BAR_AREA_W + 8}
                    y={midY - 8}
                    width={CHIP_W - 12}
                    height={16}
                    rx={4}
                    fill="var(--surface2)"
                    stroke="var(--surface3)"
                    strokeWidth={0.5}
                  />
                  <text
                    x={LABEL_W + BAR_AREA_W + 8 + (CHIP_W - 12) / 2}
                    y={midY + 4}
                    textAnchor="middle"
                    fontSize={9}
                    fill="var(--text2)"
                    fontWeight={600}
                    className="mono"
                  >
                    {CF_KIND_LABELS[step.cfKind]}
                  </text>
                </g>
              )}

              {/* Row separator */}
              <line
                x1={0}
                y1={y + ROW_H - 1}
                x2={W}
                y2={y + ROW_H - 1}
                stroke="var(--surface3)"
                strokeWidth={0.5}
                opacity={0.4}
              />
            </g>
          );
        })}

        {/* Footer: total p95 */}
        <text
          x={LABEL_W}
          y={H - 4}
          fontSize={10}
          fill="var(--text3)"
          className="mono"
        >
          Total p95: {trace.totalLatencyMs} ms · {trace.steps.length} steps · safety: {trace.safetyTier}
        </text>
      </svg>
    </div>
  );
}
